import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  CircleAlert,
  Speech,
  Smile,
  PencilLine,
  CheckCircle,
  AlignLeft,
  DatabaseZap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import {
  useRulesByIds,
  useKnowledgeBasesByIds,
} from "@/hooks/use-shadow-metadata";
import type { Message } from "@/types/shadow.types";
import type { Contact } from "@/services/message.service";

interface ConversationDetailsSidebarProps {
  messages: Message[];
  conversationId?: number;
  contact?: Contact | null;
  isLoading?: boolean;
  onSelectConversation?: (conversationId: number) => void;
  selectedMessageId?: string;
  onMessageSelect?: (messageId: string) => void;
}

export const ConversationDetailsSidebar = ({
  messages,
  conversationId,
  isLoading,
  selectedMessageId,
  onMessageSelect,
}: ConversationDetailsSidebarProps) => {
  const [currentResponseIndex, setCurrentResponseIndex] = useState(0);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);

  // Get messages with a scored shadow evaluation for analytics
  const messagesWithShadow = useMemo(() => {
    return messages.filter(
      (m) => m.shadowEval?.hasComparison && m.messageType === "outgoing",
    );
  }, [messages]);

  // Sync currentResponseIndex when selectedMessageId changes (e.g., from clicking in chat)
  useMemo(() => {
    if (selectedMessageId) {
      const index = messagesWithShadow.findIndex(
        (m) => (m.id || m.messageId) === selectedMessageId,
      );
      if (index !== -1 && index !== currentResponseIndex) {
        setCurrentResponseIndex(index);
      }
    }
  }, [selectedMessageId, messagesWithShadow, currentResponseIndex]);

  // Current response being viewed
  const currentResponse = messagesWithShadow[currentResponseIndex];
  const totalResponses = messagesWithShadow.length;

  // Handle navigation to previous/next response
  const handleNavigate = (newIndex: number) => {
    const targetMessage = messagesWithShadow[newIndex];
    if (targetMessage && onMessageSelect) {
      const messageId = targetMessage.id || targetMessage.messageId;
      onMessageSelect(messageId);
    }
    setCurrentResponseIndex(newIndex);
  };

  // Extract analytics from current response
  const analytics = useMemo(() => {
    if (!currentResponse?.shadowEval) return null;

    const shadowEval = currentResponse.shadowEval;
    const details = shadowEval.details;
    const comparison = details.comparison;

    return {
      // similarityScore: shadowEval.semanticSimilarity
      //   ? Math.round(shadowEval.semanticSimilarity * 100)
      //   : null,
      comparisonBucket: shadowEval.comparisonBucket,
      aiTone: comparison?.aiTone || null,
      humanTone: comparison?.humanTone || null,
      toneMatch: comparison?.toneMatch ?? null,
      aiSentiment: comparison?.aiSentiment || null,
      humanSentiment: comparison?.humanSentiment || null,
      personalizationAI: details.personalizationScores?.aiMessageScore || null,
      personalizationHuman:
        details.personalizationScores?.humanMessageScore || null,
      completenessAI: details.completenessScores?.aiMessageScore || null,
      completenessHuman: details.completenessScores?.humanMessageScore || null,
      lengthAI: details.lengthAnalysis?.aiMessageSentLength || null,
      lengthHuman: details.lengthAnalysis?.humanMessageSentLength || null,
      ruleIds: shadowEval.ruleIds || [],
      knowledgeBaseIds: shadowEval.knowledgeBaseIds || [],
      observability: details.observability,
    };
  }, [currentResponse]);

  // Fetch rule and knowledge base details for display
  const { data: fetchedRules = [] } = useRulesByIds(analytics?.ruleIds || []);
  const { data: fetchedKBs = [] } = useKnowledgeBasesByIds(
    analytics?.knowledgeBaseIds || [],
  );

  // Helper function to render status indicator
  const renderStatusIndicator = (isMatch: boolean | null, isGood?: boolean) => {
    if (isMatch === null) return null;

    const shouldShowGreen = isGood ?? isMatch;

    return shouldShowGreen ? (
      <div className="flex items-center justify-center w-5 h-5 rounded-full">
        <CircleCheck size={16} fill="var(--color-lime-500)" color="white" />
      </div>
    ) : (
      <div className="flex items-center justify-center w-5 h-5 rounded-full">
        <CircleAlert size={16} fill="var(--color-red-500)" color="white" />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-sm text-muted-foreground">Loading details...</div>
      </div>
    );
  }

  if (!conversationId || messagesWithShadow.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-sm text-muted-foreground text-center px-4">
          No analytics data available
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-sm text-muted-foreground text-center px-4">
          No comparison data available
        </div>
      </div>
    );
  }

  return (
    <div className="h-full relative">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 px-4 pt-4 bg-white/70 backdrop-blur-sm">
        <div className="flex items-center justify-between pb-2.5">
          <div>
            <h2 className="text-[13px] font-semibold text-black-300">
              Response {currentResponseIndex + 1}
            </h2>
            <p className="text-[12px] text-gray-800 font-normal">Analytics</p>
          </div>
          <div className="flex items-center gap-1.5 text-black-300">
            {currentResponseIndex > 0 && (
              <button
                onClick={() =>
                  handleNavigate(Math.max(0, currentResponseIndex - 1))
                }
                className="rounded hover:bg-gray-100 cursor-pointer"
              >
                <ChevronLeft
                  className="text-gray-700 hover:text-gray-900"
                  size={18}
                />
              </button>
            )}
            <span className="text-xs font-semibold">
              {currentResponseIndex + 1} / {totalResponses}
            </span>
            {currentResponseIndex < totalResponses - 1 && (
              <button
                onClick={() =>
                  handleNavigate(
                    Math.min(totalResponses - 1, currentResponseIndex + 1),
                  )
                }
                className="rounded hover:bg-gray-100 cursor-pointer"
              >
                <ChevronRight
                  className="text-gray-700 hover:text-gray-900"
                  size={18}
                />
              </button>
            )}
          </div>
        </div>
        <div className="absolute -bottom-8 left-0 right-0 h-8 bg-linear-to-b from-white/50 via-white/20 to-transparent pointer-events-none" />
      </div>

      <ScrollArea className="h-full *:data-[slot=scroll-area-scrollbar]:top-[70px]!">
        <div className="pt-[70px]">
          {/* Comparison Bucket */}
          {analytics.comparisonBucket && (
            <div className="px-4 pb-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] text-gray-800 font-medium">
                  Response Quality
                </span>
                <div
                  className={cn(
                    "inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-medium",
                    analytics.comparisonBucket === "Perfect" ||
                      analytics.comparisonBucket === "Good Enough"
                      ? "bg-lime-100 text-lime-600"
                      : "bg-amber-100 text-orange-600",
                  )}
                >
                  {analytics.comparisonBucket}
                </div>
              </div>
            </div>
          )}

          {/* Comparison Table */}
          <style>{`
          .dashed-row-border {
            border-bottom: none !important;
            background-image: linear-gradient(to right, var(--color-gray-400) 3px, transparent 3px);
            background-size: 6px 1px;
            background-repeat: repeat-x;
            background-position: bottom;
          }
        `}</style>
          <div className="border border-collapse border-none overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[80px_1fr_1fr_32px] border-y border-gray-400 bg-gray-50 py-1.5">
              <div className="pl-3 py-0.5 text-xs font-normal text-gray-700 italic">
                Criteria
              </div>
              <div className="pl-2 py-0.5 text-xs font-normal text-center text-gray-700 border-l">
                Human Agent
              </div>
              <div className="py-0.5 text-xs font-normal text-center text-gray-700">
                Kim AI
              </div>
            </div>

            {/* Tone Row */}
            <div className="grid grid-cols-[80px_1fr_1fr_32px] dashed-row-border bg-white py-1.5 h-[66px] items-center">
              <div className="py-1 pl-4 flex flex-col items-start gap-1.5 text-xs text-gray-900">
                <Speech size={16} className="text-gray-700 shrink-0" />
                <span className="font-medium text-gray-900">Tone</span>
              </div>
              {analytics.aiTone === analytics.humanTone ? (
                <div className="col-span-2 pl-2 pr-0.5 py-2 border-l flex items-center justify-center">
                  <span className="w-full text-center py-1.25 text-xs bg-gray-100 text-black-100 font-medium capitalize truncate rounded-sm">
                    {analytics.humanTone || "-"}
                  </span>
                </div>
              ) : (
                <>
                  <div className="pl-2 pr-0.5 py-2 border-l flex items-center justify-center w-full">
                    <span className="w-full text-center py-1.25 text-xs bg-gray-100 text-black-100 font-medium capitalize truncate rounded-sm">
                      {analytics.humanTone || "-"}
                    </span>
                  </div>
                  <div className="pl-0.5 pr-0.5 py-2 flex items-center justify-center w-full">
                    <span className="w-full text-center py-1.25 text-xs bg-gray-100 text-black-100 font-medium capitalize truncate rounded-sm">
                      {analytics.aiTone || "-"}
                    </span>
                  </div>
                </>
              )}
              <div className="pr-2 flex items-center justify-start">
                {renderStatusIndicator(
                  analytics.aiTone === null || analytics.humanTone === null
                    ? null
                    : analytics.toneMatch,
                )}
              </div>
            </div>

            {/* Sentiment Row */}
            <div className="grid grid-cols-[80px_1fr_1fr_32px] dashed-row-border bg-white w-full py-1.5 h-[66px] items-center">
              <div className="py-1 pl-4 flex flex-col items-start gap-1.5 text-xs text-gray-900">
                <Smile size={14} className="text-gray-700 shrink-0" />
                <span className="font-medium text-gray-900">Sentiment</span>
              </div>
              {analytics.aiSentiment === analytics.humanSentiment ? (
                <div className="col-span-2 px-3 py-2 border-l flex items-center justify-center w-full">
                  <span className="w-full text-center py-1.25 text-xs bg-gray-100 text-black-100 font-medium capitalize truncate rounded-sm">
                    {analytics.humanSentiment || "-"}
                  </span>
                </div>
              ) : (
                <>
                  <div className="pl-2 pr-0.5 py-2 border-l flex items-center justify-center w-full">
                    <span className="w-full text-center py-1.25 text-xs bg-gray-100 text-black-100 font-medium capitalize truncate rounded-sm">
                      {analytics.humanSentiment || "-"}
                    </span>
                  </div>
                  <div className="pl-0.5 pr-0.5 py-2 flex items-center justify-center w-full">
                    <span className="w-full text-center py-1.25 text-xs bg-gray-100 text-black-100 font-medium capitalize truncate rounded-sm">
                      {analytics.aiSentiment || "-"}
                    </span>
                  </div>
                </>
              )}
              <div className="pr-2 flex items-center justify-start">
                {renderStatusIndicator(
                  analytics.aiSentiment === null ||
                    analytics.humanSentiment === null
                    ? null
                    : analytics.aiSentiment === analytics.humanSentiment,
                )}
              </div>
            </div>

            {/* Personalization Row */}
            <div className="grid grid-cols-[80px_1fr_1fr_32px] dashed-row-border bg-white py-1.5 h-[66px] items-center">
              <div className="py-1 pl-4 flex flex-col items-start gap-1.5 text-xs text-gray-900">
                <PencilLine size={14} className="text-gray-700 shrink-0" />
                <span className="font-medium leading-tight">
                  Personalis
                  <wbr />
                  ation
                </span>
              </div>
              {analytics.personalizationAI ===
              analytics.personalizationHuman ? (
                <div className="col-span-2 pl-2 pr-0.5 py-2 border-l flex items-center justify-center">
                  <div className="w-full text-center py-[0.350rem] text-xs bg-lime-100 text-lime-600 font-medium capitalize truncate rounded-sm">
                    {analytics.personalizationHuman}%
                  </div>
                </div>
              ) : (
                <>
                  <div className="pl-2 pr-0.5 py-2 border-l flex items-center">
                    {analytics.personalizationHuman !== null && (
                      <div
                        className={cn(
                          "w-full text-center py-[0.350rem] text-xs font-medium capitalize truncate rounded-sm",
                          analytics.personalizationHuman >
                            analytics.personalizationAI!
                            ? "bg-lime-100 text-lime-600"
                            : "bg-amber-100 text-orange-600",
                        )}
                      >
                        {analytics.personalizationHuman}%
                      </div>
                    )}
                  </div>
                  <div className="pl-0.5 pr-0.5 py-2 flex items-center">
                    {analytics.personalizationAI !== null && (
                      <div
                        className={cn(
                          "w-full text-center py-[0.350rem] text-xs font-medium capitalize truncate rounded-sm",
                          analytics.personalizationAI >
                            analytics.personalizationHuman!
                            ? "bg-lime-100 text-lime-600"
                            : "bg-amber-100 text-orange-600",
                        )}
                      >
                        {analytics.personalizationAI}%
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="pr-2 flex items-center justify-start">
                {renderStatusIndicator(
                  analytics.personalizationAI === null ||
                    analytics.personalizationHuman === null
                    ? null
                    : analytics.personalizationAI >=
                        analytics.personalizationHuman,
                )}
              </div>
            </div>

            {/* Completeness Row */}
            <div className="grid grid-cols-[80px_1fr_1fr_32px] dashed-row-border bg-white py-1.5 h-[66px] items-center">
              <div className="py-1 pl-4 flex flex-col items-start gap-1.5 text-xs text-gray-900">
                <CheckCircle size={14} className="text-gray-700 shrink-0" />
                <span className="leading-tight font-medium">
                  Complete
                  <wbr />
                  ness
                </span>
              </div>
              {analytics.completenessAI === analytics.completenessHuman ? (
                <div className="col-span-2 pl-2 pr-0.5 py-2 border-l flex items-center justify-center">
                  <div className="w-full text-center py-[0.350rem] text-xs bg-lime-100 text-lime-600 font-medium capitalize truncate rounded-sm">
                    {analytics.completenessHuman}%
                  </div>
                </div>
              ) : (
                <>
                  <div className="pl-2 pr-0.5 py-2 border-l flex items-center">
                    {analytics.completenessHuman !== null && (
                      <div
                        className={cn(
                          "w-full text-center py-[0.350rem] text-xs font-medium capitalize truncate rounded-sm",
                          analytics.completenessHuman >
                            analytics.completenessAI!
                            ? "bg-lime-100 text-lime-600"
                            : "bg-amber-100 text-orange-600",
                        )}
                      >
                        {analytics.completenessHuman}%
                      </div>
                    )}
                  </div>
                  <div className="pl-0.5 pr-0.5 py-2 flex items-center">
                    {analytics.completenessAI !== null && (
                      <div
                        className={cn(
                          "w-full text-center py-[0.350rem] text-xs font-medium capitalize truncate rounded-sm",
                          analytics.completenessAI >
                            analytics.completenessHuman!
                            ? "bg-lime-100 text-lime-600"
                            : "bg-amber-100 text-orange-600",
                        )}
                      >
                        {analytics.completenessAI}%
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="pr-2 flex items-center justify-start">
                {renderStatusIndicator(
                  analytics.completenessAI === null ||
                    analytics.completenessHuman === null
                    ? null
                    : analytics.completenessAI >= analytics.completenessHuman,
                )}
              </div>
            </div>

            {/* Length Row */}
            <div className="grid grid-cols-[80px_1fr_1fr_32px] bg-white border-b border-gray-400 py-1.5 h-[66px] items-center">
              <div className="py-1 pl-4 flex flex-col items-start gap-1.5 text-xs text-gray-900">
                <AlignLeft size={14} className="text-gray-700 shrink-0" />
                <span className="font-medium text-gray-900">Length</span>
              </div>
              {analytics.lengthAI === analytics.lengthHuman ? (
                <div className="col-span-2 pl-2 pr-0.5 py-2 border-l flex items-center justify-center">
                  <span className="w-full text-center py-1.25 text-xs bg-gray-100 text-black-100 font-medium capitalize truncate rounded-sm">
                    {analytics.lengthHuman || "-"}
                  </span>
                </div>
              ) : (
                <>
                  <div className="pl-2 pr-0.5 py-2 border-l flex items-center justify-center w-full">
                    <span className="w-full text-center py-1.25 text-xs bg-gray-100 text-black-100 font-medium capitalize truncate rounded-sm">
                      {analytics.lengthHuman || "-"}
                    </span>
                  </div>
                  <div className="pl-0.5 pr-0.5 py-2 flex items-center justify-center w-full">
                    <span className="w-full text-center py-1.25 text-xs bg-gray-100 text-black-100 font-medium capitalize truncate rounded-sm">
                      {analytics.lengthAI || "-"}
                    </span>
                  </div>
                </>
              )}
              <div className="pr-2 flex items-center justify-start">
                {renderStatusIndicator(
                  analytics.lengthAI === null || analytics.lengthHuman === null
                    ? null
                    : analytics.lengthAI === analytics.lengthHuman,
                )}
              </div>
            </div>
          </div>

          <div className="spacey-y-6">
            {/* Instructions Used */}
            <Collapsible
              open={instructionsOpen}
              onOpenChange={setInstructionsOpen}
            >
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between p-4 hover:cursor-pointer transition-colors">
                  <h3 className="text-[13px] font-semibold text-black-300">
                    Instructions Used
                  </h3>
                  <ChevronRight
                    size={24}
                    className={cn(
                      "transition-transform text-gray-700 shrink-0 p-1 rounded-full hover:bg-gray-50",
                      instructionsOpen && "transform rotate-90",
                    )}
                  />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-5 px-4 pb-4">
                  {fetchedRules.length > 0 ? (
                    fetchedRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="pl-2.5 text-xs text-black-300 font-normal capitalize"
                      >
                        {rule.name || rule.id}
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-700 py-2">
                      No instructions used
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Knowledge Used */}
            <Collapsible
              open={knowledgeOpen}
              onOpenChange={setKnowledgeOpen}
              className="border-y"
            >
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between p-4 hover:cursor-pointer transition-colors">
                  <h3 className="text-[13px] font-semibold text-black-300">
                    Knowledge Used
                  </h3>
                  <ChevronRight
                    size={24}
                    className={cn(
                      "transition-transform text-gray-700 shrink-0 p-1 rounded-full hover:bg-gray-50",
                      knowledgeOpen && "transform rotate-90",
                    )}
                  />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="flex flex-wrap gap-1.5 px-4 pb-4">
                  {fetchedKBs.length > 0 ? (
                    fetchedKBs.map((kb) => (
                      <Badge
                        key={kb.id}
                        variant="secondary"
                        className="flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full text-xs text-black-50"
                      >
                        <div className="bg-gray-300 p-1 rounded-full">
                          <DatabaseZap size={12} />
                        </div>
                        <span className="truncate">{kb.name || kb.id}</span>
                      </Badge>
                    ))
                  ) : (
                    <div className="text-xs text-gray-700 py-2">
                      No knowledge base used
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
